import { trpc } from '@klear/ui/lib/trpc'
import Papa from 'papaparse'
import { Timestamp } from '@klear/time'
import { DEFAULT_FINANCIAL_HISTORY_WINDOW_RANGE } from '@klear/constants'
import { useMemo } from 'react'
import { useKlearFinancialAccount } from '@klear/ui/hooks/use-klear-financial-account'

export function useKlearAccountTransactions(dateRange: number = DEFAULT_FINANCIAL_HISTORY_WINDOW_RANGE) {
  const { selectedAccountId, klearFinancialAccount } = useKlearFinancialAccount()
  const accountId = selectedAccountId ?? klearFinancialAccount?.id ?? undefined
  const hasAccountId = accountId != null && accountId > 0

  const {
    data: transactionList,
    error,
    isPending,
    isFetching,
    isSuccess
  } = trpc.klearAccount.getTransactions.useQuery(
    {
      days: dateRange,
      limit: 100,
      klearAccountId: accountId!
    },
    {
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      enabled: hasAccountId
    }
  )

  const transactions = useMemo(() => transactionList ?? [], [transactionList])

  const downloadTransactions = () => {
    if (!transactions.length) return

    const transactionsData = transactions.map((t) => ({
      date: t.createdAt.toKlearDate().toString(),
      status: t.status,
      direction: t.isOutbound ? 'Outbound' : 'Inbound',
      description: t.description,
      amount: t.amount.toString()
    }))

    const csv = Papa.unparse(transactionsData, {
      header: true,
      delimiter: ','
    })

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.href = url
    link.download = `klear-account-transactions-${Timestamp.now().toKlearDate().toString()}.csv`

    link.click()
    URL.revokeObjectURL(url)
  }

  return {
    transactions,
    error,
    isLoading: !hasAccountId || isPending || (isFetching && !isSuccess),
    isFetching,
    downloadTransactions
  }
}
